import { Checkbox } from '@deliveryhero/pd-cookbook/components/Checkbox';
import IcCheckmark from '@deliveryhero/pd-cookbook/components/Icons/IcCheckmark';
import { Typography } from '@deliveryhero/pd-cookbook/components/Typography';
import {
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select
} from '@mui/material';

import { FormikProps } from 'formik';
import HighlightAsterisks from 'src/components/HighlightAsterisks/HighlightAsterisks';

import ButtonList from '../../ButtonListSelect';
import { AllowanceValuesType } from '../AllowanceDetailForm';
import { AllowancePaddedPaper as PaddedPaper } from './AllowancePaddedPaper';
import { applicableWeekdaysButtons } from './buttons';
import { Timeslot } from './constants';

export const SetApplicableDayTime = ({
  values,
  setFieldValue,
  setValues,
  touched,
  errors
}: FormikProps<AllowanceValuesType>) => {
  const roundMinutesToThirty = (time: string) => {
    const splitTime = time.split(':');
    if (splitTime[0] == '23' && splitTime[1] == '59') {
      return '23:59';
    }
    const min = parseInt(splitTime[1]);
    return splitTime[0] + ':' + (min >= 15 && min < 45 ? '30' : '00');
  };

  return (
    <Grid item>
      <PaddedPaper
        title={'Employees can use allowance on *'}
        disabled={!values.editable}
      >
        <Grid item xs={12}>
          <ButtonList
            buttons={applicableWeekdaysButtons.map((b) =>
              values.applicable_weekdays &&
              values.applicable_weekdays.includes(b.id)
                ? { ...b, selected: true }
                : b
            )}
            handleClick={(id) => {
              values.applicable_weekdays.includes(id)
                ? values.applicable_weekdays.length > 1 &&
                  setFieldValue(
                    'applicable_weekdays',
                    values.applicable_weekdays.filter((s) => s !== id)
                  )
                : setFieldValue('applicable_weekdays', [
                    ...values.applicable_weekdays,
                    id
                  ]);
            }}
            selectedIcon={<IcCheckmark style={{ fill: '#E21B70' }} />}
            allowMultipleSelection
            disabled={!values.editable}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography type="title-md">
            <HighlightAsterisks
              text="Orders can be delivered between *"
              disabled={!values.editable}
            />
          </Typography>
        </Grid>
        <Grid container item spacing={2}>
          <Grid item xs>
            <FormControl
              fullWidth
              error={Boolean(errors.applicable_time_from)}
              disabled={!values.editable}
            >
              {values.editable ? (
                <InputLabel
                  id="select-start-time-label"
                  sx={{
                    textTransform: 'capitalize',
                    '&.MuiInputLabel-shrink': {
                      color: '#E21B70'
                    }
                  }}
                >
                  Start time
                </InputLabel>
              ) : null}

              <Select
                labelId="select-group-label"
                value={
                  values.applicable_time_from.length === 0
                    ? '08:00'
                    : roundMinutesToThirty(values.applicable_time_from)
                }
                onChange={(e) => {
                  setValues({
                    ...values,
                    applicable_all_day_time: false,
                    applicable_time_from: e.target.value as string
                  });
                }}
                input={
                  values.editable ? (
                    <OutlinedInput
                      label="Start time"
                      inputProps={{
                        'aria-label': `select-start-time`
                      }}
                    />
                  ) : undefined
                }
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#E21B70'
                  }
                }}
              >
                {Timeslot.map((time) => (
                  <MenuItem key={time} value={`${time}`}>
                    {time}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{errors.applicable_time_from}</FormHelperText>
            </FormControl>
          </Grid>

          <Grid
            container
            item
            spacing={0}
            alignItems="center"
            justifyContent="center"
            width={28}
          >
            <Grid item xs>
              <Divider
                textAlign="center"
                sx={{
                  maxWidth: 12
                }}
              />
            </Grid>
          </Grid>

          <Grid item xs>
            <FormControl
              fullWidth
              error={Boolean(errors.applicable_time_to)}
              disabled={!values.editable}
            >
              {values.editable ? (
                <InputLabel
                  id="select-start-time-label"
                  sx={{
                    textTransform: 'capitalize',
                    '&.MuiInputLabel-shrink': {
                      color: '#E21B70'
                    }
                  }}
                  disabled={!values.editable}
                >
                  End time
                </InputLabel>
              ) : null}

              <Select
                labelId="select-group-label"
                value={
                  values.applicable_time_to.length === 0
                    ? '18:00'
                    : roundMinutesToThirty(values.applicable_time_to)
                }
                onChange={(e) => {
                  setValues({
                    ...values,
                    applicable_all_day_time: false,
                    applicable_time_to: e.target.value as string
                  });
                }}
                input={
                  values.editable ? (
                    <OutlinedInput
                      label="End time"
                      inputProps={{
                        'aria-label': `select-end-time`
                      }}
                    />
                  ) : undefined
                }
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#E21B70'
                  }
                }}
              >
                {Timeslot.map((time) => (
                  <MenuItem key={time} value={`${time}`}>
                    {time}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{errors.applicable_time_to}</FormHelperText>
            </FormControl>
          </Grid>

          <Grid
            container
            item
            spacing={0}
            alignItems="center"
            justifyContent="center"
            width={28}
            xs
          >
            <Grid
              item
              xs
              sx={{
                '>label >div': {
                  marginLeft: 0
                }
              }}
            >
              <Checkbox
                id="all-day-checkbox"
                label="All day"
                checked={values.applicable_all_day_time || false}
                onChange={(e) => {
                  e.target.checked
                    ? setValues({
                        ...values,
                        applicable_all_day_time: true,
                        applicable_time_from: '00:00',
                        applicable_time_to: '23:59'
                      })
                    : setValues({
                        ...values,
                        applicable_all_day_time: false,
                        applicable_time_from: '09:30',
                        applicable_time_to: '18:00'
                      });
                }}
                disabled={!values.editable}
              />
            </Grid>
          </Grid>
        </Grid>
      </PaddedPaper>
    </Grid>
  );
};
